import { ScatterChart, Scatter, XAxis, YAxis, ResponsiveContainer, Tooltip, ZAxis } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface RevenuePotentialChartProps {
  data: Array<{
    companyName: string;
    employeeCount: number;
    revenueRange: string;
    revenueScore: number;
  }>;
}

// Map revenue ranges to numeric values for x-axis
const REVENUE_MAP: Record<string, number> = {
  'under-1M': 0.5,
  '1M-5M': 3,
  '5M-10M': 7.5,
  '10M-50M': 30,
  '50M-100M': 75,
  '100M+': 150,
};

export function RevenuePotentialChart({ data }: RevenuePotentialChartProps) {
  const chartData = data
    .filter((item) => item.employeeCount > 0 && item.revenueRange)
    .map((item) => ({
      x: REVENUE_MAP[item.revenueRange] || 0,
      y: item.employeeCount,
      z: item.revenueScore,
      name: item.companyName,
      revenueRange: item.revenueRange,
    }))
    .sort((a, b) => b.z - a.z); // Sort by revenue score descending

  return (
    <Card>
      <CardHeader>
        <CardTitle>Revenue Potential Analysis</CardTitle>
        <CardDescription>Employee count vs. Revenue range (size = revenue score)</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <ScatterChart margin={{ top: 20, right: 30, bottom: 60, left: 20 }}>
            <XAxis
              type="number"
              dataKey="x"
              name="Revenue Range"
              label={{ value: 'Revenue Range (Millions)', position: 'insideBottom', offset: -10, fill: 'hsl(var(--muted-foreground))' }}
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `$${value}M`}
            />
            <YAxis
              type="number"
              dataKey="y"
              name="Employee Count"
              label={{ value: 'Employees', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))' }}
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
            />
            <ZAxis
              type="number"
              dataKey="z"
              range={[100, 500]}
              name="Revenue Score"
            />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              formatter={(value: any, name?: string) => { // eslint-disable-line @typescript-eslint/no-explicit-any
                if (name === 'Revenue Range') {
                  return `$${value}M`;
                }
                if (name === 'Employee Count') {
                  return `${value} employees`;
                }
                if (name === 'Revenue Score') {
                  return `Score: ${value.toFixed(1)}`;
                }
                return value.toString();
              }}
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
                color: 'hsl(var(--foreground))',
              }}
              labelFormatter={(label) => chartData.find((d) => d.x === label)?.name || ''}
            />
            <Scatter
              data={chartData}
              fill="hsl(var(--primary))"
              fillOpacity={0.6}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
